import { QueryJournalEvent } from '../../api/schema';

// Entity types the journal records, as the backend names them
// (backend/pkg/db/repository/journal). The label is what the Affected filter
// and the table show.
export const ENTITY_TYPES: { key: string; label: string }[] = [
  { key: 'site', label: 'Job site' },
  { key: 'assignment', label: 'Delivery' },
  { key: 'location_visit', label: 'Site visit' },
  { key: 'item', label: 'Tool / item' },
  { key: 'product', label: 'Product' },
  { key: 'manufacturer', label: 'Manufacturer' },
  { key: 'customer', label: 'Customer' },
  { key: 'user', label: 'User' },
  { key: 'vehicle', label: 'Vehicle' },
  { key: 'room', label: 'Room' },
  { key: 'warehouse', label: 'Vehicle / room' },
  { key: 'device', label: 'Device' },
  { key: 'maintenance_event', label: 'Maintenance event' },
  { key: 'batch_report', label: 'Report' },
  { key: 'upload', label: 'File upload' },
  { key: 'image', label: 'Image' },
  { key: 'alert_monitor', label: 'Alert subscriptions' },
  { key: 'messaging_settings', label: 'Messaging settings' },
  { key: 'organization', label: 'Organization' },
  { key: 'feature', label: 'Feature' },
  { key: 'vertical_market', label: 'Vertical market' },
  { key: 'embedded_build', label: 'Device build' },
];

export const entityLabel = (key?: string): string => ENTITY_TYPES.find((t) => t.key === key)?.label ?? key ?? '';

// Where a record of each type lives in the app. Types without a detail page
// return undefined; admin-only pages are returned only for admin viewers.
export const entityHref = (type: string | undefined, id: string | undefined, isAdmin: boolean): string | undefined => {
  if (!type || !id) {
    return undefined;
  }
  switch (type) {
    case 'site':
      return `/customers/job-sites/${id}/details`;
    case 'assignment':
      // A delivery has no page of its own; its job site's Assigned Equipment tab is the closest.
      return undefined;
    case 'location_visit':
      return `/activity/site-visits/${id}/details`;
    case 'item':
      return `/tools/items/${id}/details`;
    case 'product':
      return `/tools/products/${id}/details`;
    case 'manufacturer':
      return `/tools/manufacturers/${id}/details`;
    case 'customer':
      return `/customers/customers/${id}/details`;
    case 'user':
      return `/company/users/${id}/details`;
    case 'vehicle':
      return `/company/vehicles/${id}/details`;
    case 'room':
      return `/company/rooms/${id}/details`;
    case 'batch_report':
      return `/reporting/batch/${id}/details`;
    case 'maintenance_event':
      return '/events/maintenance';
    case 'device':
      return isAdmin ? `/admin/organizations/devices/${id}/details` : undefined;
    case 'organization':
      return isAdmin ? `/admin/organizations/organizations/${id}/details` : undefined;
    case 'feature':
      return isAdmin ? `/admin/vertical-markets/features/${id}/details` : undefined;
    case 'vertical_market':
      return isAdmin ? `/admin/vertical-markets/markets/${id}/details` : undefined;
    case 'embedded_build':
      return isAdmin ? `/admin/embedded-builds/builds/${id}/details` : undefined;
    default:
      return undefined;
  }
};

// The link for the row's main record: a delivery links through its job site
// (undelivered) or its site visit (delivered), both carried as the related
// record.
export const rowHref = (row: QueryJournalEvent, isAdmin: boolean): string | undefined => {
  if (row.entity_type === 'assignment') {
    return row.related_exists ? entityHref(row.related_type, row.related_id, isAdmin) : undefined;
  }
  if (row.entity_type === 'image') {
    return row.related_exists ? entityHref(row.related_type, row.related_id, isAdmin) : undefined;
  }
  if (row.entity_type === 'warehouse') {
    return row.related_exists ? entityHref(row.related_type, row.related_id, isAdmin) : undefined;
  }
  return row.entity_exists ? entityHref(row.entity_type, row.entity_id, isAdmin) : undefined;
};

export const relatedHref = (row: QueryJournalEvent, isAdmin: boolean): string | undefined =>
  row.related_exists ? entityHref(row.related_type, row.related_id, isAdmin) : undefined;
